import classNames from 'classnames';
import { BlockEmbed } from 'quill/blots/block';
import Embed from 'quill/blots/embed';
import type Header from 'quill/formats/header';
import type { ErrorInfo } from 'react';
import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import alertIcon from '../icons/alert.svg';
import Logger from '../utils/Logger';
import styles from './EditorBlotErrorBoundary.module.scss';
import type { PortalBlot } from './QuillEditor';

interface EditorBlotErrorBoundaryProps {
  blot: Embed | BlockEmbed | Header | PortalBlot;
  children: React.ReactNode;
}

interface EditorBlotErrorBoundaryState {
  hasError: boolean;
}

class EditorBlotErrorBoundary extends Component<
  EditorBlotErrorBoundaryProps,
  EditorBlotErrorBoundaryState
> {
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  constructor(props: EditorBlotErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const name = `editor:${this.props.blot.statics.blotName}`;
    Logger.fatal(error, errorInfo, { tags: { errorBoundary: name } });
  }

  render() {
    const { blot, children } = this.props;
    if (!this.state.hasError) {
      return children;
    }

    const title = 'Uh oh!';
    const message = `This ${blot.statics.blotName} is having issues loading. The error has been logged and we’ll be on it!`;

    if (blot instanceof BlockEmbed) {
      return createPortal(
        <div className={classNames(styles.container, styles.block)}>
          <SVGIcon className={styles.icon}>{alertIcon}</SVGIcon>
          <div className={styles.content}>
            {title}
            <div className={styles.message}>{message}</div>
          </div>
        </div>,
        blot.domNode,
      );
    }

    if (blot instanceof Embed) {
      return createPortal(
        <Tooltip
          trigger="hover"
          tooltip={message}
          placement="top"
          popOverPortal={document.body}
        >
          <div className={styles.container}>
            <SVGIcon inline className={styles.icon}>
              {alertIcon}
            </SVGIcon>
            <div className={styles.content}>{title}</div>
          </div>
        </Tooltip>,
        blot.contentNode,
      );
    }

    return null;
  }
}

export default EditorBlotErrorBoundary;
